import { useParams, Link, useNavigate } from "react-router-dom";
import { CONDITIONS, CLINIC, FAQS, CITY_PAGES } from "@/data/clinic";
import { ArrowRight, CheckCircle2, AlertCircle, ClipboardList, Stethoscope, Phone, ShieldCheck, Heart, Sparkles, MessageCircle } from "lucide-react";
import AppointmentForm from "@/components/AppointmentForm";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import SEOHead, { physicianSchema, localBusinessSchema } from "@/components/SEOHead";

export default function TreatmentPage({ slug: slugProp }) {
  const params = useParams();
  const nav = useNavigate();
  const slug = slugProp || params.slug;
  const c = CONDITIONS.find((x) => x.slug === slug);

  if (!c) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-32 text-center">
        <h1 className="font-poppins text-3xl text-slate-900">Treatment not found</h1>
        <p className="text-slate-600 mt-2">The page you're looking for doesn't exist.</p>
        <button onClick={() => nav("/")} className="mt-6 bg-green-700 text-white rounded-full px-6 py-3 text-sm">Back to home</button>
      </div>
    );
  }

  const pageFaqs = FAQS.slice(0, 6);

  const Section = ({ icon: Icon, title, items, intro }) => (
    <section className="py-12 border-t border-slate-100">
      <div className="grid lg:grid-cols-3 gap-10">
        <div>
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl grid place-items-center text-white bg-green-700">
              <Icon className="w-5 h-5" />
            </div>
            <h2 className="font-poppins font-semibold text-2xl sm:text-3xl text-slate-900">{title}</h2>
          </div>
          {intro && <p className="text-slate-600 mt-4 text-sm leading-relaxed">{intro}</p>}
        </div>
        <ul className="lg:col-span-2 grid sm:grid-cols-2 gap-x-8 gap-y-3">
          {items.map((i) => (
            <li key={i} className="flex gap-3 text-slate-700">
              <CheckCircle2 className="w-5 h-5 text-green-700 shrink-0 mt-0.5" />
              <span>{i}</span>
            </li>
          ))}
        </ul>
      </div>
    </section>
  );

  return (
    <div data-testid={`treatment-page-${c.slug}`}>
      <SEOHead
        title={c.metaTitle}
        description={c.metaDesc}
        path={`/${c.slug}`}
        image={c.image}
        breadcrumbs={[
          { name: "Home", url: "/" },
          { name: "Treatments", url: "/" },
          { name: c.title, url: `/${c.slug}` },
        ]}
        faqs={pageFaqs}
        schemas={[
          { id: "ld-physician", data: physicianSchema() },
          { id: "ld-localbiz", data: localBusinessSchema() },
        ]}
      />

      {/* Breadcrumb */}
      <nav className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 text-xs text-slate-500">
        <Link to="/" className="hover:text-green-700">Home</Link>
        <span className="mx-1.5">/</span>
        <span className="text-slate-700">{c.title}</span>
      </nav>

      {/* Hero */}
      <section className="bg-gradient-to-br from-green-50/70 via-white to-green-50/40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 grid lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="inline-flex items-center gap-2 bg-green-50 text-green-700 px-3.5 py-1.5 rounded-full text-xs font-medium tracking-wide">
              <ShieldCheck className="w-3.5 h-3.5" /> Senior Specialist • 20+ Years
            </div>
            <h1 className="font-poppins font-semibold text-4xl sm:text-5xl lg:text-6xl text-slate-900 mt-4 leading-[1.05] tracking-tight">
              {c.h1}
            </h1>
            <p className="mt-5 text-slate-600 text-lg leading-relaxed">{c.short}</p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/contact" data-testid={`treatment-book-${c.slug}`} className="inline-flex items-center gap-2 bg-green-700 hover:bg-green-800 text-white rounded-full px-7 py-3.5 font-medium text-sm shadow">
                Book Appointment <ArrowRight className="w-4 h-4" />
              </Link>
              <a href={`tel:+91${CLINIC.phone}`} className="inline-flex items-center gap-2 border-2 border-slate-900 text-slate-900 hover:bg-slate-900 hover:text-white rounded-full px-7 py-3 font-medium text-sm">
                <Phone className="w-4 h-4" /> Call Now
              </a>
              <a href={`https://wa.me/${CLINIC.whatsapp}`} target="_blank" rel="noreferrer" className="inline-flex items-center gap-2 bg-[#25D366] hover:bg-emerald-500 text-white rounded-full px-6 py-3 font-medium text-sm">
                <MessageCircle className="w-4 h-4" /> WhatsApp
              </a>
            </div>
          </div>
          <div className="lg:col-span-5">
            <img src={c.image} alt={c.title} className="w-full h-[380px] object-cover rounded-3xl border border-slate-100 shadow-lg" />
          </div>
        </div>
      </section>

      {/* Introduction */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-14">
        <h2 className="font-poppins font-semibold text-2xl sm:text-3xl text-slate-900">Introduction</h2>
        {c.intro.map((p, i) => (
          <p key={i} className="mt-4 text-slate-600 text-lg leading-relaxed">{p}</p>
        ))}
        <p className="mt-4 text-slate-600 text-base leading-relaxed">
          To book a consultation directly, visit our <Link to="/contact" className="text-green-700 underline">Contact page</Link>, or learn more <Link to="/about-dr-ashutosh-mishra" className="text-green-700 underline">about Dr. Ashutosh Mishra</Link>.
        </p>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Section icon={AlertCircle} title="Symptoms" items={c.symptoms} intro="Recognise the warning signs early — that's when treatment is easiest and most effective." />
        <Section icon={ClipboardList} title="Causes" items={c.causes} intro="Understanding the underlying causes helps create a targeted treatment plan." />
        <Section icon={Heart} title="Risk Factors" items={c.riskFactors} intro="Knowing your personal risk profile guides screening and prevention." />
        <Section icon={Stethoscope} title="Diagnosis" items={c.diagnosis} intro="The right tests, interpreted correctly, are the foundation of effective treatment." />
        <Section icon={CheckCircle2} title="Treatment Options" items={c.treatments} intro="Evidence-based, individualised treatment plans suited to Indian lifestyles." />
        <Section icon={Sparkles} title="Benefits of Early Treatment" items={c.benefits} intro="Catching and treating it early protects you from costly long-term complications." />
      </div>

      {/* Why Choose Dr Mishra */}
      <section className="bg-green-50/50 py-16 mt-10">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-start">
          <div>
            <h2 className="font-poppins font-semibold text-3xl sm:text-4xl text-slate-900">Why Choose Dr. Ashutosh Mishra</h2>
            {c.whyChoose.map((p, i) => (
              <p key={i} className="mt-4 text-slate-600 leading-relaxed">{p}</p>
            ))}
            <ul className="mt-6 space-y-3">
              {[
                "Personalised treatment plans — never one-size-fits-all",
                "Integrated diabetes, endocrine and kidney care under one roof",
                "Modern protocols including CGM, GLP-1 and SGLT2 therapies",
                "WhatsApp follow-up support for established patients",
              ].map((t) => (
                <li key={t} className="flex gap-3 text-slate-700">
                  <CheckCircle2 className="w-5 h-5 text-green-700 shrink-0 mt-0.5" />{t}
                </li>
              ))}
            </ul>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/about-dr-ashutosh-mishra" className="text-sm text-green-700 underline">More about Dr. Ashutosh Mishra →</Link>
              <Link to="/patient-testimonials" className="text-sm text-green-700 underline">Read patient testimonials →</Link>
            </div>
          </div>
          <AppointmentForm />
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-poppins font-semibold text-3xl sm:text-4xl text-slate-900 text-center">Frequently asked questions</h2>
          <Accordion type="single" collapsible className="mt-8">
            {pageFaqs.map((f, i) => (
              <AccordionItem key={i} value={`faq-${i}`} className="border-b border-slate-200">
                <AccordionTrigger className="text-left font-medium text-slate-900 hover:text-green-700">{f.q}</AccordionTrigger>
                <AccordionContent className="text-slate-600 leading-relaxed">{f.a}</AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
          <div className="mt-8 text-center">
            <Link to="/faqs" className="text-sm text-green-700 inline-flex items-center gap-1">See all FAQs <ArrowRight className="w-4 h-4" /></Link>
          </div>
        </div>
      </section>

      {/* Related Treatments + City Pages */}
      <section className="bg-slate-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-poppins font-semibold text-2xl text-slate-900">Explore related treatments</h2>
          <div className="mt-6 grid md:grid-cols-3 gap-4">
            {c.relatedSlugs.slice(0, 3).map((sl) => {
              const r = CONDITIONS.find((x) => x.slug === sl);
              if (!r) return null;
              return (
                <Link key={sl} to={`/${sl}`} className="bg-white border border-slate-100 rounded-2xl p-5 hover:shadow-md transition flex gap-4 items-center">
                  <img src={r.image} alt={r.title} className="w-16 h-16 object-cover rounded-xl" />
                  <div>
                    <div className="font-poppins font-semibold text-base text-slate-900">{r.title}</div>
                    <div className="text-xs text-green-700 mt-1 inline-flex items-center gap-1">Read more <ArrowRight className="w-3 h-3" /></div>
                  </div>
                </Link>
              );
            })}
          </div>
          <div className="mt-10 grid md:grid-cols-2 gap-4">
            {CITY_PAGES.map((p) => (
              <Link key={p.slug} to={`/${p.slug}`} className="bg-white border border-slate-100 rounded-2xl p-5 hover:shadow-md transition">
                <div className="font-poppins font-semibold text-lg text-slate-900">{p.title}</div>
                <div className="text-xs text-green-700 mt-1">View specialist page →</div>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA band */}
      <section className="bg-slate-950 text-white py-14">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-poppins font-semibold text-3xl">Ready to take the first step?</h2>
          <p className="text-slate-300 mt-3">Book a consultation with {CLINIC.doctorName} at our Kabir Nagar clinic, Varanasi.</p>
          <div className="mt-7 flex flex-wrap gap-3 justify-center">
            <Link to="/contact" className="bg-green-500 hover:bg-green-400 text-slate-900 rounded-full px-7 py-3.5 font-medium text-sm">Book Appointment</Link>
            <a href={`tel:+91${CLINIC.phone}`} className="border border-white/30 hover:bg-white/10 rounded-full px-7 py-3.5 font-medium text-sm">Call {CLINIC.phoneDisplay}</a>
          </div>
        </div>
      </section>
    </div>
  );
}
